import React, { useEffect, useState } from 'react';
import { X, Download, Play, ExternalLink, FileText, Target, BookOpen, Video as VideoIcon, Link as LinkIcon, Image as ImageIcon } from 'lucide-react';
import MainLayout from '../layout/MainLayout';
import { downloadResource } from './ResourceCard';
import resourcesApi, { Resource, youtubeId, driveId } from '../../services/resourcesApi';
import { TYPE_LABEL } from './ResourceCard';
import { formatDate } from '../../utils/formatters';
import '../../styles/mentee-dashboard.scss';
import '../plan/plan.scss';
import '../../styles/resources.scss';

interface Props {
  eyebrow: string;
  lead: string;
}


/** Acento por tipo, para que la tarjeta se lea de un vistazo. */
const ACCENT: Record<string, string> = {
  document: '#C8102E', video: '#0F4E8A', link: '#1F6F4A', image: '#8B5A2B', other: '#3A4660',
};

const CARD_ICON: Record<string, React.ElementType> = {
  document: Target, video: VideoIcon, link: LinkIcon, image: ImageIcon, other: BookOpen,
};

/** Etiqueta corta del formato: PDF, MP4, YOUTUBE, ENLACE… */
const formatBadge = (r: Resource): string => {
  if (youtubeId(r.url)) return 'YOUTUBE';
  if (driveId(r.url)) return 'VIDEO';
  if (r.url) return 'ENLACE';
  const ext = (r.file_name || '').split('.').pop();
  return ext ? ext.toUpperCase() : (TYPE_LABEL[r.type] || 'ARCHIVO').toUpperCase();
};

const sizeLabel = (bytes?: number | null) => {
  if (!bytes) return null;
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ResourceTile: React.FC<{ resource: Resource; onPlay: (r: Resource) => void; onDownloaded: () => void }> =
  ({ resource: r, onPlay, onDownloaded }) => {
    const accent = ACCENT[r.type] || ACCENT.other;
    const Icon = CARD_ICON[r.type] || BookOpen;
    // Solo los recursos de tipo video se reproducen embebidos; un documento en Drive se abre como enlace.
    const playable = r.type === 'video' && (!!youtubeId(r.url) || !!driveId(r.url) || !!r.file_path);

    const handleDownload = async () => {
      try { await downloadResource(r); onDownloaded(); }
      catch (e) { console.error('Error downloading resource:', e); }
    };

    return (
      <div className="rec-card" style={{ ['--accent' as any]: accent, ['--accent-fill' as any]: `${accent}14` }}>
        <div className="rec-card-head">
          <div className="rec-card-icon"><Icon size={22} color={accent} strokeWidth={1.6} /></div>
          <div className="rec-card-meta">
            <div className="rec-card-cat">{(TYPE_LABEL[r.type] || 'RECURSO').toUpperCase()}</div>
            <span className="rec-card-type">{formatBadge(r)}</span>
          </div>
        </div>

        <div>
          <div className="rec-card-title">{r.title}</div>
        </div>

        {r.description && <div className="rec-card-desc">{r.description}</div>}

        <div className="rec-card-foot">
          <div>
            <div className="rec-card-pages">
              <FileText size={12} strokeWidth={1.5} />
              {sizeLabel(r.file_size) || (r.url ? 'Recurso en línea' : 'Archivo')}
            </div>
            <div className="rec-card-aud">{formatDate(r.created_at).toUpperCase()}</div>
          </div>

          {playable ? (
            <button className="rec-download" onClick={() => onPlay(r)}>
              <Play size={13} strokeWidth={2} /> Ver video
            </button>
          ) : r.url ? (
            <a className="rec-download" href={r.url} target="_blank" rel="noopener noreferrer">
              <ExternalLink size={13} strokeWidth={2} /> Abrir recurso
            </a>
          ) : (
            <button className="rec-download" onClick={handleDownload}>
              <Download size={13} strokeWidth={2} /> Descargar material
            </button>
          )}
        </div>
      </div>
    );
  };

/** Vista de solo lectura de la biblioteca: la usan mentor y mentee. */
const ResourceLibrary: React.FC<Props> = ({ eyebrow, lead }) => {
  const [resources, setResources] = useState<Resource[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [playing, setPlaying] = useState<Resource | null>(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const rs = await resourcesApi.list();
      setResources(rs);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudieron cargar los recursos');
    } finally {
      setLoading(false);
    }
  };

  const visible = resources
    .filter(r => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return r.title.toLowerCase().includes(q) || (r.description || '').toLowerCase().includes(q);
    })
    // El taller de mentores (lo más antiguo) va primero, y las sesiones en orden.
    .sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));

  return (
    <MainLayout>
      <div className="mentee-dashboard sessions-view">
        <div className="sv-eyebrow">{eyebrow}</div>
        <h1 className="sv-title">Recursos del <em>Programa</em></h1>
        <p className="sv-lead">{lead}</p>

        <div className="sessions-page">
          {loading ? (
            <p className="dash-empty">Cargando recursos…</p>
          ) : (
            <>
              {error && <div className="sf-empty">{error}</div>}

              <div className="res-toolbar">
                <div className="res-toolbar-right">
                  <input className="sf-input res-search" placeholder="Buscar recurso…"
                    value={search} onChange={e => setSearch(e.target.value)} />
                </div>
              </div>

              {visible.length === 0 ? (
                <p className="dash-empty">
                  {resources.length === 0
                    ? 'Todavía no hay recursos publicados. El equipo del programa los irá compartiendo aquí.'
                    : 'Ningún recurso coincide con el filtro.'}
                </p>
              ) : (
                <div className="rec-grid">
                  {visible.map(r => (
                    <ResourceTile key={r.id} resource={r} onPlay={setPlaying} onDownloaded={load} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {playing && <VideoModal resource={playing} onClose={() => setPlaying(null)} />}
      </div>
    </MainLayout>
  );
};

/** Reproductor: YouTube embebido o el mp4 servido por la API. */
export const VideoModal: React.FC<{ resource: Resource; onClose: () => void }> = ({ resource, onClose }) => {
  const ytId = youtubeId(resource.url);
  const gdId = driveId(resource.url);
  const embedded = ytId
    ? `https://www.youtube.com/embed/${ytId}`
    : gdId ? `https://drive.google.com/file/d/${gdId}/preview` : null;
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;
    if (!embedded && resource.file_path) {
      // El archivo requiere el token, así que se pide por la API y se reproduce en memoria.
      resourcesApi.download(resource.id)
        .then(res => {
          objectUrl = window.URL.createObjectURL(new Blob([res.data], { type: resource.file_name?.endsWith('.webm') ? 'video/webm' : 'video/mp4' }));
          setSrc(objectUrl);
        })
        .catch(() => setSrc(null));
    }
    return () => { if (objectUrl) window.URL.revokeObjectURL(objectUrl); };
  }, [resource, embedded]);

  return (
    <div className="feedback-modal-overlay" onClick={onClose}>
      <div className="feedback-modal res-video-modal" onClick={e => e.stopPropagation()}>
        <button className="sf-close" onClick={onClose} aria-label="Cerrar"><X size={18} /></button>
        <h3>{resource.title}</h3>
        <div className="res-video-frame">
          {embedded ? (
            <iframe
              src={embedded}
              title={resource.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : src ? (
            <video src={src} controls autoPlay />
          ) : (
            <p className="dash-empty">Cargando video…</p>
          )}
        </div>
        {resource.description && <p className="feedback-modal__sub">{resource.description}</p>}
      </div>
    </div>
  );
};

export default ResourceLibrary;
